<template>
  <el-drawer
    title="Add New Beneficiary"
    :visible.sync="showDrawer"
    :with-header="true"
    :before-close="handleClose"
    :size="drawerWidth"
  >
    <div class="w-full h-screen">
      <div class="flex gap-x-2 background p-1.5 rounded-lg mx-4 lg:mx-8 mb-8">
        <div
          class="flex-1 flex justify-center text-13px py-2.5 cursor-pointer transition-all duration-150 ease-linear"
          @click="setActiveClass('fiat')"
          :class="
            isFiatAccount
              ? 'bg-white rounded-lg text-primary font-bold shadow-md'
              : 'font-normal'
          "
        >
          Fiat Accounts
        </div>
        <div
          class="flex-1 flex justify-center text-13px py-2.5 cursor-pointer transition-all duration-150 ease-linear"
          @click="setActiveClass('asset')"
          :class="
            isAssetWallet
              ? 'bg-white rounded-lg text-primary font-bold shadow-md'
              : 'font-normal'
          "
          v-if="tenantPermission('asset-access')"
        >
          Asset Wallets
        </div>
      </div>

      <div class="flex flex-col mx-4 lg:mx-8 mb-8" v-if="isFiatAccount">
        <p class="flex items-center gap-x-1 mb-3">
          <span class="text-gray-muted font-medium text-sm">Currency</span>
        </p>
        <div class="relative">
          <p
            class="flex items-center gap-x-1 font-normal lg:gap-x-2 absolute left-4 top-2 z-10 bg-white"
          >
            <currency-icons
              size="lg:h-8 lg:w-8 h-5 w-5"
              :currency="getCurrencyCountry(selectedCurrency)"
            />
            <span class="text-black-10">{{ selectedCurrency }}</span>
          </p>

          <el-select
            id="select"
            v-model="selectedCurrency"
            class="w-full"
            @change="handleCurrencyChange"
          >
            <el-option
              v-for="(account, index) in currencyOptions"
              :value="account.currency"
              :key="index"
              style="margin-bottom: 0.5rem"
            >
              <div class="flex items-center gap-x-2">
                <currency-icons
                  :currency="getCurrencyCountry(account.currency)"
                />
                <span>{{ account.currency }}</span>
              </div>
            </el-option>
          </el-select>
        </div>
      </div>

      <div
        class="flex flex-col mx-4 lg:mx-8 mb-8"
        v-if="isFiatAccount && hasPaymentMethods"
      >
        <p class="flex items-center gap-x-1 mb-3">
          <span class="text-gray-muted font-medium text-sm"
            >Payment Method</span
          >
        </p>
        <el-select
          v-model="selectedPaymentMethod"
          @change="handlePaymentMethod"
        >
          <el-option
            v-for="(paymentMethod, index) in paymentMethods"
            :key="index"
            :label="paymentMethod.label"
            :value="paymentMethod.value"
          >
            {{ paymentMethod.label }}
          </el-option>
        </el-select>
      </div>

      <div class="w-full bg-root">
        <div class="px-4 lg:px-8 pb-8" v-if="isFiatAccount">
          <ngn-payout-beneficiary-form
            v-if="currencyIsNGN"
            @next="addNewBeneficiary"
            :isSavingBeneficiary="isSavingBeneficiary"
          />
          <foreign-payout-beneficiary-form
            v-if="!transferDestinationIsLocal"
            @next="addNewBeneficiary"
            :isSavingBeneficiary="isSavingBeneficiary"
          />
          <mobile-money-payout-beneficiary-form
            v-if="isMobileMoneyPayoutForm || isXofMnoForm"
            @next="addNewBeneficiary"
            :isSavingBeneficiary="isSavingBeneficiary"
          />
          <bank-transfer-payout-beneficiary-form
            v-if="isBankTransferPayoutMethod && !isXofMnoForm"
            @next="addNewBeneficiary"
            :isSavingBeneficiary="isSavingBeneficiary"
          />
        </div>
        <div class="px-4 lg:px-8 pb-8" v-else>
          <asset-account-form @proceedToAddWithdrawal="addAssetBeneficiary" />
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import breakpoints from '@/utils/breakpoints.js'
import bulkPayout from '@/mixins/bulkPayout'
import tenant from '@/mixins/tenant.js'
import getBalance from '@/mixins/getBalance.js'
import { GetCurrencyCountry } from '@/utils/helpers'
import {
  PAYMENT_METHODS,
  BULK_PAYOUT_PAYMENT_CURRENCIES,
  FIAT,
  ASSETS
} from '@/utils/constants.js'

export default {
  mixins: [bulkPayout, tenant, getBalance],
  props: {
    showDrawer: {
      type: Boolean,
      required: true
    },
    type: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      isSavingBeneficiary: false,
      activeClass: 'fiat',
      selectedCurrency: '',
      selectedPaymentMethod: '',
      paymentMethods: PAYMENT_METHODS
    }
  },
  computed: {
    drawerWidth () {
      return breakpoints.lgAndUp ? '35%' : breakpoints.mdAndUp ? '70%' : '90%'
    },
    isMobileMoneyPayoutForm () {
      return (
        this.mobileWalletSupportedCurrencies && this.isMobileMoneyPayoutMethod
      )
    },
    isXofMnoForm () {
      return (
        (this.currencyIsXOF || this.currencyIsXAF) &&
        this.selectedPaymentMethod === 'mno'
      )
    },
    isFiatAccount () {
      return this.activeClass === 'fiat'
    },
    isAssetWallet () {
      return this.activeClass === 'asset'
    },
    hasPaymentMethods () {
      return BULK_PAYOUT_PAYMENT_CURRENCIES.includes(
        this.selectedCurrency || ''
      )
    },
    currencyOptions () {
      return this.isFiatAccount ? FIAT : ASSETS
    }
  },
  components: {
    NgnPayoutBeneficiaryForm: () =>
      import('@/components/v2/bulkPayout/forms/NgnPayoutBeneficiaryForm.vue'),
    ForeignPayoutBeneficiaryForm: () =>
      import(
        '@/components/v2/bulkPayout/forms/ForeignPayoutBeneficiaryForm.vue'
      ),
    MobileMoneyPayoutBeneficiaryForm: () =>
      import(
        '@/components/v2/bulkPayout/forms/MobileMoneyPayoutBeneficiaryForm.vue'
      ),
    BankTransferPayoutBeneficiaryForm: () =>
      import(
        '@/components/v2/bulkPayout/forms/BankTransferPayoutBeneficiaryForm.vue'
      ),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    AssetAccountForm: () =>
      import(
        '@/components/v2/withdrawal/drawers/withdrawalAccountForm/AssetAccountForm.vue'
      )
  },
  methods: {
    handleClose () {
      this.$emit('closeDrawer')
    },
    async addNewBeneficiary (recipientInfo) {
      try {
        await this.saveBeneficiary(recipientInfo, this.selectedCurrency)
        this.$emit('beneficiarySaved')
      } catch (error) {
        this.handleErrorResponseNotification(error)
      }
    },
    setActiveClass (arg) {
      this.selectedCurrency = '' // reset selected currency when the use changes tab
      this.activeClass = arg
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    clearSelectedBeneficiary () {
      this.$store.dispatch('bulkPayout/setBeneficiaryDetails', null)
    },
    clearSavedBeneficiaries () {
      this.$store.dispatch('bulkPayout/removeSelectedBeneficiaries', [])
    },
    async handleCurrencyChange () {
      this.clearSelectedBeneficiary()
      this.clearSavedBeneficiaries()
      // this condition is to cater for situations where a user had selected ghs and mobile money type.... but decides to change it to ngn ...
      //  So it sets the paymentMethod to an empty string at change
      if (
        this.selectedCurrency === this.$CURRENCY.NGN ||
        this.selectedCurrency === this.$CURRENCY.USD
      ) {
        this.selectedPaymentMethod = ''
      }
      // this is to cater for when we clear the user's previous selection but to get the document template for XAF and XOF, we can't pass empty string
      // BE is expecting mno
      if (
        this.selectedCurrency === this.$CURRENCY.XAF ||
        this.selectedCurrency === this.$CURRENCY.XOF
      ) {
        this.selectedPaymentMethod = 'mno'
      }
      this.$store.dispatch(
        'bulkPayout/savePayoutCurrency',
        this.selectedCurrency
      )
      await this.getBulkPayoutConfig()
    },
    async handlePaymentMethod () {
      // await this.getBulkPayoutConfig()
      this.$store.dispatch(
        'bulkPayout/savePaymentMethod',
        this.selectedPaymentMethod
      )
    },
    async addAssetBeneficiary (payload) {
      try {
        await this.$store
          .dispatch('bulkPayout/saveBeneficiary', {
            walletDetails: {
              ...payload.beneficiaryDetails.walletDetails,
              walletTag:
                payload.beneficiaryDetails.walletDetails.walletTag || null
            },
            destinationCurrency:
              payload.beneficiaryDetails.walletDetails.assetType
          })
          .then(() => {
            this.$emit('closeDrawer')
            this.$emit('beneficiarySaved')
          })
      } catch (error) {
        this.handleErrorResponseNotification(error)
      }
    }
  },
  mounted () {
    this.setActiveClass(this.type)
  }
}
</script>

<style scoped>
* >>> .el-drawer__header > :first-child {
  font-size: 0.9rem;
  color: var(--text-2);
  font-weight: 600;
}
* >>> .el-drawer__header {
  color: var(--text-4);
  font-size: 1.2rem;
  padding-bottom: 1rem;
  padding-top: 2rem;
  padding-left: 1.4rem;
  font-weight: 700;
  border-bottom: 1px solid var(--border-2);
}
* >>> .el-drawer {
  background: var(--background-9) !important;
}
.background {
  background: var(--background-muted);
}
</style>
